<template>
  <div
    class="button-toggle"
    :class="{ 'show-toggle': isToggled, 'hide-toggle': !isToggled, disabled: isDisabled }"
  >
    <label class="toggle-switch-wrapper" v-if="isToggled">
      <input
        :checked="relation === 'AND'"
        :disabled="isDisabled"
        type="checkbox"
        @change="handleChange($event)"
      />
      <span class="slider"></span>
      <span class="or-label rbz-text-regular" :class="{ active: relation === 'OR' }">or</span>
      <span class="and-label rbz-text-regular" :class="{ active: relation === 'AND' }">and</span>
    </label>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { Relation } from '@/types';

interface ToggleButton {
  relation?: Relation;
  disabled?: boolean;
  ignoreUpdateInProcess?: boolean;
  isToggled?: boolean;
}

const props = withDefaults(defineProps<ToggleButton>(), {
  relation: 'OR',
  disabled: false,
});

interface FormButtonToggleEmits {
  (e: 'update:selector', value: Relation): void;
}

const emit = defineEmits<FormButtonToggleEmits>();

const planetStore = usePlanetStore();
const isDisabled = computed(() => {
  return props.disabled || (planetStore.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const handleChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  const newRelation = isChecked ? 'AND' : 'OR';
  emit('update:selector', newRelation);
};
</script>

<style lang="scss" scoped>
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.button-toggle {
  padding: 10px 8px;
  width: 100%;
  min-height: 60px;
  display: flex;
  justify-content: end;
  border-left: 0.5px solid map.get(colors.$colors, 'silver');
}

.show-toggle {
  background-color: map.get(colors.$colors, 'alabaster');
}

.hide-toggle {
  background-color: map.get(colors.$colors, 'white');
}

.toggle-switch-wrapper {
  position: relative;
  display: inline-block;
  width: 103px;
  height: 40px;
  float: right;
}

.toggle-switch-wrapper input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: map.get(colors.$colors, 'alto');
  transition: 0.4s;
  border-radius: 4px;

  &::before {
    position: absolute;
    content: '';
    height: 36px;
    width: 42px;
    left: 2px;
    bottom: 2px;
    background-color: map.get(colors.$colors, 'fun-blue');
    transition: 0.4s;
    border-radius: 4px 0 0 4px;
  }
}

input:checked + .slider {
  background-color: map.get(colors.$colors, 'alto');

  &::before {
    transform: translateX(46px);
    border-radius: 0 4px 4px 0;
    width: 53px;
  }
}

.or-label.active,
.and-label.active {
  color: map.get(colors.$colors, 'white');
}

.or-label,
.and-label {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: map.get(colors.$colors, 'stratos');
  cursor: pointer;
}

.or-label {
  left: 16px;
}

.and-label {
  right: 16px;
}
</style>
